import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import NProgress from 'nprogress';
import type { ApiResponse, ErrorResponse } from '../types/api';
import type { RefreshTokenResponse } from '../types/auth';
import { AUTH_ENDPOINTS } from '../constants/api';

const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
const adminBase = `${apiBase}/api/admin`;
const publicBase = `${apiBase}/api/v1`;

const TOKEN_KEY = 'odc_access_token';
const REFRESH_TOKEN_KEY = 'odc_refresh_token';
const TOKEN_EXPIRY_KEY = 'odc_token_expiry';
const REMEMBER_ME_KEY = 'odc_remember_me';

let pendingRequests = 0;
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function incrementPendingRequests() {
  if (pendingRequests === 0) {
    NProgress.start();
  }
  pendingRequests += 1;
}

function decrementPendingRequests() {
  pendingRequests -= 1;
  if (pendingRequests <= 0) {
    pendingRequests = 0;
    NProgress.done();
  }
}

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function getStoredAccessToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  return token ? token.trim() : null;
}

function getStoredRefreshToken(): string | null {
  const token = localStorage.getItem(REFRESH_TOKEN_KEY);
  return token ? token.trim() : null;
}

function storeTokens(token: string, refreshToken?: string, expiresAt?: string) {
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  const storage = rememberMe ? localStorage : sessionStorage;

  if (rememberMe) {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  storage.setItem(TOKEN_KEY, token.trim());
  if (expiresAt) {
    storage.setItem(TOKEN_EXPIRY_KEY, expiresAt.trim());
  }

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken.trim());
  }
}

function clearTokens() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REMEMBER_ME_KEY);
}

async function refreshAccessToken(oldToken: string, refreshToken: string): Promise<RefreshTokenResponse> {
  const response = await axios.post<ApiResponse<RefreshTokenResponse>>(
    `${adminBase}${AUTH_ENDPOINTS.REFRESH_TOKEN}`,
    { token: oldToken, refreshToken }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Token refresh failed');
  }

  return response.data.data;
}

function attachInterceptors(instance: AxiosInstance, includeAuth: boolean) {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      incrementPendingRequests();

      if (includeAuth) {
        const token = getStoredAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => {
      decrementPendingRequests();
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      decrementPendingRequests();
      return response;
    },
    async (error: AxiosError<ErrorResponse | ApiResponse<unknown>>) => {
      decrementPendingRequests();

      const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
      const status = error.response?.status;

      if (includeAuth && status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        // Don't retry if this is the refresh endpoint itself
        if (originalRequest.url?.includes('/auth/refresh')) {
          clearTokens();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              instance(originalRequest)
                .then(resolve)
                .catch(reject);
            });
          });
        }

        isRefreshing = true;

        try {
          const oldToken = getStoredAccessToken();
          const refreshToken = getStoredRefreshToken();

          if (!oldToken || !refreshToken) {
            throw new Error('No refresh token available');
          }

          const refreshed = await refreshAccessToken(oldToken, refreshToken);
          storeTokens(refreshed.token, refreshed.refreshToken, refreshed.expiresAt);

          // Update the failed request's auth header
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${refreshed.token}`;
          }

          onRefreshed(refreshed.token);

          return instance(originalRequest);
        } catch (refreshError) {
          clearTokens();
          // Clear the subscriber queue
          refreshSubscribers = [];
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      const isCanceled =
        error.code === 'ERR_CANCELED' ||
        error.name === 'CanceledError' ||
        (error as unknown as { message?: string }).message === 'canceled';

      if (!isCanceled) {
        const apiMessage = (error.response?.data as ErrorResponse | undefined)?.message;
        const message = apiMessage || error.message;
        // eslint-disable-next-line no-console
        console.error('[HTTP ERROR]', status, message, error.config?.url);
      }

      return Promise.reject(error);
    }
  );
}

const adminHttp = axios.create({
  baseURL: adminBase,
  headers: {
    'Content-Type': 'application/json',
  },
});

// public v1
const publicHttp = axios.create({
  baseURL: publicBase,
  headers: {
    'Content-Type': 'application/json',
  },
});
const publicV2Http = axios.create({
  baseURL: `${apiBase}/api/v2`,
  headers: {
    'Content-Type': 'application/json',
  },
});

attachInterceptors(adminHttp, true);
attachInterceptors(publicHttp, false);
attachInterceptors(publicV2Http, false);

export const http = adminHttp;
export { adminHttp, publicHttp, publicV2Http };
export default adminHttp;

export function isAxiosNotFound(err: unknown): boolean {
  const e = err as AxiosError | undefined;
  return !!e && !!e.response && e.response.status === 404;
}

export function isAxiosCanceled(err: unknown): boolean {
  const e = err as AxiosError | undefined;
  return !!e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || (e as any)?.message === 'canceled');
}
